import { useCallback, useEffect, useState } from 'preact/hooks';

interface StaleCacheState {
  isStale: boolean;
  oldestAgeMs: number | null;
}

interface UseStaleCacheBannerResult extends StaleCacheState {
  dismiss: () => void;
}

// Listens for 'stale-cache-served' postMessage events from the service worker
// (see public/sw.js → notifyStale). Tracks the oldest age across events so the
// banner can show the worst-case staleness, not just the latest one.
export function useStaleCacheBanner(): UseStaleCacheBannerResult {
  const [state, setState] = useState<StaleCacheState>({ isStale: false, oldestAgeMs: null });

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'stale-cache-served') return;
      const ageMs = typeof event.data.ageMs === 'number' ? event.data.ageMs : 0;
      setState((prev) => ({
        isStale: true,
        oldestAgeMs: prev.oldestAgeMs == null ? ageMs : Math.max(prev.oldestAgeMs, ageMs),
      }));
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const dismiss = useCallback(() => {
    setState({ isStale: false, oldestAgeMs: null });
  }, []);

  return { ...state, dismiss };
}
